/**
 * MCPToolsBrowser Component
 *
 * Modal for browsing and selecting MCP servers and tools
 * Migrated from tool-browser.js createMcp() and HTML templates
 */

import React from "react";
import { Modal, Loading, ErrorMessage } from "../../../components/common";
import { ToolItem } from "./ToolItem";
import { SelectedToolsList } from "./SelectedToolsList";
import { useMCPBrowser } from "../hooks";
import { useI18n } from "@/i18n";
import type { MCPServersConfig } from "../../../types/tools";

export interface MCPToolsBrowserProps {
  isOpen: boolean;
  initialConfig?: MCPServersConfig | string;
  onClose: () => void;
  onApply: (config: string) => void;
}

export const MCPToolsBrowser: React.FC<MCPToolsBrowserProps> = ({
  isOpen,
  initialConfig,
  onClose,
  onApply,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig.mcpBrowser as
    | Record<string, string>
    | undefined;
  const {
    // State
    activeTab,
    setActiveTab,

    // Data
    groupedMcpTools,
    status,
    warnings,
    failedServers,

    // Computed
    selectedToolCount,
    generatedMcpConfig,

    // Validation
    invalidServers,
    invalidTools,
    hasInvalidItems,

    // Loading/Error
    isLoading,
    error,

    // Actions
    toggleServer,
    toggleMcpTool,
    isServerSelected,
    isMcpToolEnabled,
    refreshMcpConnections,
    handleApply,
    selectedServers,
    enabledMcpTools,
  } = useMCPBrowser({
    initialConfig,
    onApply,
  });

  const handleApplyClick = () => {
    handleApply();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <div className="space-y-4">
        {/* Header with refresh button */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t?.title}</h3>
          <button
            onClick={refreshMcpConnections}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t?.refresh}
          </button>
        </div>

        {/* Status warnings */}
        {(status === "partial" || status === "error") &&
          warnings.length > 0 && (
            <ErrorMessage
              error={warnings}
              type={status === "error" ? "error" : "warning"}
            />
          )}

        {/* Tab selector */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("system")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "system"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t?.systemServers}
          </button>
          <button
            onClick={() => setActiveTab("user")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "user"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t?.userServers}
          </button>
        </div>

        {/* Selected count */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {t?.serversSelected?.replace(
              "{count}",
              String(selectedServers.length)
            )}
          </span>
          <span>
            {t?.toolsSelected?.replace("{count}", String(selectedToolCount))}
          </span>
        </div>

        {/* Loading state */}
        {isLoading && <Loading message={t?.loadingServers} />}

        {/* Error state */}
        {error && <ErrorMessage error={error} />}

        {/* Content */}
        {!isLoading && !error && (
          <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
            {/* Left: Available servers (2 columns) */}
            <div className="col-span-2 space-y-4">
              <h4 className="font-semibold text-gray-900">
                {t?.availableServers}
              </h4>

              {groupedMcpTools.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {t?.noServersAvailable}
                </div>
              ) : (
                <div className="space-y-4">
                  {groupedMcpTools.map((server) => (
                    <div
                      key={server.server_id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      {/* Server header */}
                      <div className="flex items-start gap-3 mb-3">
                        <input
                          type="checkbox"
                          checked={isServerSelected(server.server_id)}
                          onChange={() => toggleServer(server.server_id)}
                          className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">
                              {server.server_id}
                            </h4>
                            <span
                              className={`w-2 h-2 rounded-full ${
                                server.connected ? "bg-green-500" : "bg-red-500"
                              }`}
                              title={
                                server.connected
                                  ? t?.connected
                                  : t?.disconnected
                              }
                            />
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                              {server.server_type}
                            </span>
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              {server.transport}
                            </span>
                            <span>
                              {t?.toolsCount?.replace(
                                "{count}",
                                String(server.tool_count)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Server tools */}
                      {server.tools.length > 0 && (
                        <div className="space-y-2 mt-3 pl-7">
                          {server.tools.map((tool) => (
                            <ToolItem
                              key={tool.namespaced_name || tool.name}
                              name={tool.name}
                              displayName={tool.display_name || undefined}
                              description={
                                tool.display_description || tool.description
                              }
                              enabled={isMcpToolEnabled(
                                tool.namespaced_name || tool.name
                              )}
                              onToggle={() =>
                                toggleMcpTool(
                                  tool.namespaced_name || tool.name,
                                  server.server_id
                                )
                              }
                              connected={tool.connected}
                              transport={tool.transport}
                            />
                          ))}
                        </div>
                      )}

                      {/* Failed server indicator */}
                      {failedServers.includes(server.server_id) && (
                        <div className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">
                          {t?.failedToConnect}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Selected servers/tools (1 column) */}
            <div className="col-span-1 border-l border-gray-200 pl-4">
              <h4 className="font-semibold text-gray-900 mb-3">
                {t?.selectedItems}
              </h4>

              {/* Warning for invalid items */}
              {hasInvalidItems && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">
                    {t?.invalidItemsWarning ||
                      "Some selected servers/tools no longer exist. Please remove them before applying."}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {/* Selected servers */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    {t?.servers}
                  </h5>
                  <SelectedToolsList
                    selectedTools={selectedServers}
                    onRemove={toggleServer}
                    emptyMessage={t?.noServersSelected}
                    invalidTools={invalidServers}
                  />
                </div>

                {/* Selected tools */}
                {enabledMcpTools.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      {t?.individualTools}
                    </h5>
                    <SelectedToolsList
                      selectedTools={enabledMcpTools}
                      onRemove={(toolName) => {
                        // Find server for this tool
                        // Remove from enabled tools
                        toggleMcpTool(toolName, "");
                      }}
                      invalidTools={invalidTools}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Preview generated config */}
        {generatedMcpConfig && (
          <div className="border-t border-gray-200 pt-4">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                {t?.previewConfig}
              </summary>
              <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                {generatedMcpConfig}
              </pre>
            </details>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            {t?.cancel}
          </button>
          <button
            onClick={handleApplyClick}
            disabled={hasInvalidItems}
            className={`px-4 py-2 rounded transition-colors ${
              hasInvalidItems
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-500 text-white hover:bg-green-600"
            }`}
            title={
              hasInvalidItems
                ? t?.removeInvalidItemsFirst || "Remove invalid items first"
                : undefined
            }
          >
            {t?.apply}
          </button>
        </div>
      </div>
    </Modal>
  );
};
